import React, { useCallback, useEffect, useState, memo } from "react";
import ReactFlow, { Handle, Controls, Background, MiniMap, useNodesState } from "reactflow";
import "reactflow/dist/style.css";
import { Card, CardContent, CardHeader, TextField, Typography } from "@mui/material";
import { useRecoilState, useRecoilValue } from "recoil";
import { botFlowDataAtom, flowObjAtom, flowValidationAtom, setUpFlowNodeEdgeAtom } from "../../../Services/_states";
import { FormData } from "../../../interfaces/Flowbuilder.interface";
import { FlowBuilderActions } from "../../../Services/_actions";

interface NodeData {
  id: string;
  type: string;
  header?: string;
  body?: string;
  footer?: string;
  configuration_name?: string;
  onChange: (id: string, updates: Partial<NodeData>) => void;
}
const MAX_LENGTHS: Record<keyof NodeData, number> = {
  body: 4096,
  header: 60,
  footer: 60,
};

const NodeCard = memo(({ title, data, fields, hasHandles = {} }: {
  title: string;
  data: NodeData;
  fields: (keyof NodeData)[];
  hasHandles?: { source?: boolean; target?: boolean };
}) => {
  const [flowValidationObj,setFlowValidationObj] = useRecoilState(flowValidationAtom);
  const [localValues, setLocalValues] = useState<Partial<NodeData>>({
    header: data.header || "",
    body: data.body || "",
    footer: data.footer || "",
    configuration_name: data.configuration_name || "",
  });

  // Sync local state with external state initially
  useEffect(() => {
    setLocalValues({
      header: data.header || "",
      body: data.body || "",
      footer: data.footer || "",
      configuration_name: data.configuration_name || "",
    });
  }, [data.header, data.body, data.footer, data.configuration_name]);

  const handleLocalChange = (field: keyof NodeData, value: string) => {
    const maxLength = MAX_LENGTHS[field] || Infinity;
    if (value.length <= maxLength) {
      setLocalValues((prev) => ({ ...prev, [field]: value }));
    };
    setFlowValidationObj((prev) => ({
      ...prev,
      [data?.id]: {
        ...prev[data?.id],
        [field]: undefined, // Clear the error
      },
    }));
  
  };

  const handleBlur = (field: keyof NodeData) => {
    if (data[field] !== localValues[field]) {
      data.onChange(data.id, { [field]: localValues[field] });
    }
  };

  return (
    <Card sx={{ padding: 2, width: 250 }}>
      <CardHeader title={title} />
      <CardContent>
        {fields.map((field) => (
          <div key={field} style={{ position: "relative" }}>
            <TextField
              label={field?.charAt(0)?.toUpperCase() + field.slice(1)}
              fullWidth
              multiline={field === "body"}
              minRows={field === "body" ? 2 : 1}
              value={localValues[field] || ""}
              onChange={(e) => handleLocalChange(field, e.target.value)}
              onBlur={() => handleBlur(field)} // Update parent only on blur
              sx={{ marginBottom: 2 }}
              margin="dense"
              variant="outlined"
              size="small"
            />

            {/* Character Counter */}
            {field === "body" && (
              <span
                style={{
                  position: "absolute",
                  right: "10px",
                  bottom: "14px",
                  fontSize: "12px",
                  color: localValues.body?.length >= 4096 ? "red" : "#888",
                }}
              >
                {`${localValues.body?.length || 0}/4096`}
              </span>
            )}

            {(field === "header" || field === "footer") && (
              <span
                style={{
                  position: "absolute",
                  right: "10px",
                  bottom: "14px",
                  fontSize: "12px",
                  color: localValues[field]?.length >= 60 ? "red" : "#888",
                }}
              >
                {`${localValues[field]?.length || 0}/60`}
              </span>
            )}
            {flowValidationObj[data?.id]?.[field] && (
              <Typography variant="body2" color="error" style={{ fontSize: '12px' }}>
                {flowValidationObj[data?.id]?.[field]}
              </Typography>
            )}
          </div>
        ))}
      </CardContent>
      {hasHandles.target && <Handle type="target" position="left" />}
      {hasHandles.source && <Handle type="source" position="right" />}
    </Card>
  );
});

const AddressNode: React.FC<{ data: NodeData }> = memo(({ data }) => (
  <NodeCard title="Address" data={data} fields={["header", "body", "footer"]} hasHandles={{ source: true }} />
));

const OrderDetailsNode: React.FC<{ data: NodeData }> = memo(({ data }) => (
  <NodeCard title="Order Details" data={data} fields={["header", "body", "footer", "configuration_name"]} hasHandles={{ source: true, target: true }} />
));

const OrderStatusNode: React.FC<{ data: NodeData }> = memo(({ data }) => (
  <NodeCard title="Order Status" data={data} fields={["header", "body", "footer"]} hasHandles={{ target: true }} />
));

const FlowCanvas: React.FC = () => {
  const actions = FlowBuilderActions();
  const [nodes, setNodes, onNodesChange] = useNodesState<NodeData[]>([]);
  const [flowObj, setFlowObj] = useRecoilState<any>(setUpFlowNodeEdgeAtom);
  const proOptions = { hideAttribution: true };


  // const handleNodeChange = useCallback(
  //   (id: string, updates: Partial<NodeData>) => {
  //     setNodes((nds) =>
  //       nds.map((node) =>
  //         node.id === id
  //           ? {
  //               ...node,
  //               data: {
  //                 ...node.data,
  //                 ...updates,
  //               },
  //             }
  //           : node
  //       )
  //     );
  //   },
  //   [setNodes]
  // );
  const handleNodeChange = useCallback(
    (id: string, updates: Partial<NodeData>) => {
      setNodes((nds) => {
        const updatedNodes = nds.map((node) =>
          node.id === id
            ? {
              ...node,
              data: {
                ...node.data,
                ...updates,
              },
            }
            : node
        );

        // Update flowObj with the new nodes
        setFlowObj((prevFlowObj) => ({
          ...prevFlowObj,
          nodes: updatedNodes, // Update the nodes key in flowObj
        }));

        return updatedNodes;
      });
    },
    [setNodes, setFlowObj]
  );

  const staticNode: any = [
    { id: "1", position: { x: 100, y: 50 }, data: { id: "1", type: "address", header: "", body: "", footer: "", onChange: handleNodeChange }, type: "address" },
    { id: "2", position: { x: 400, y: 50 }, data: { id: "2", type: "orderDetails", header: "", body: "", footer: "", configuration_name: "", onChange: handleNodeChange }, type: "orderDetails" },
    { id: "3", position: { x: 700, y: 50 }, data: { id: "3", type: "orderStatus", body: "", header: "", footer: "", onChange: handleNodeChange }, type: "orderStatus" },
  ];
  const edges = [
    { id: "e1-2", source: "1", target: "2", animated: false, type: "smoothstep" },
    { id: "e2-3", source: "2", target: "3", animated: false, type: "smoothstep" }
  ];

  // useEffect(() => {
  // if(!flowObj || flowObj?.nodes?.length<1){
  //   setNodes(staticNode);
  //   setFlowObj({...flowObj,nodes:staticNode,edges:edges});  
  // } 
  // }, [handleNodeChange]);

  useEffect(() => {
    if (flowObj?.nodes?.length > 0) {
      const updatedNodes = flowObj?.nodes?.map((node) => ({
        ...node,
        data: {
          ...node.data,
          onChange: handleNodeChange,
        },
      }));
      setNodes(updatedNodes);
    } else {
      setNodes(staticNode);
      setFlowObj({ ...flowObj, nodes: staticNode, edges: edges });
    }
  }, [flowObj])



  const nodeTypes = { address: AddressNode, orderDetails: OrderDetailsNode, orderStatus: OrderStatusNode };

  return (
    <div style={{ height: "500px" }}>
      <ReactFlow nodes={nodes} edges={edges} nodeTypes={nodeTypes} proOptions={proOptions} fitView>
        {/* <Controls />
        <MiniMap /> */}
        <Background />
      </ReactFlow>
    </div>
  );
};

export default FlowCanvas;
